import { PhoneCall, LineChart, BarChart3, Sun } from 'lucide-react'
import type { LandingPersona } from '@/hooks/useLandingPersona'

interface Props {
  persona: LandingPersona
}

export default function LandingDemo({ persona }: Props) {
  return (
    <section className="lp-section" id="demo">
      <div className="lp-container">
        <div className="lp-section-header">
          <div className="lp-eyebrow">A call with {persona.name}</div>
          <h2>
            Not a chatbot. <span style={{ color: persona.primary }}>Your dashboard, vocalized.</span>
          </h2>
          <p className="lp-subtitle">
            You call. {persona.name} reads your live data and tells you what’s
            happening — out loud, in plain language, wherever you are.
          </p>
        </div>

        <div className="lp-demo">
          <div className="lp-card lp-chat">
            <div>
              <div className="lp-bubble-meta">07:42 · Morning brief</div>
              <div className="lp-bubble lp-bubble-ai">
                Good morning. Overnight: 2 new trials, $79 in new MRR, ad spend at
                $138 with a 2.4× ROAS. One ad set — Lookalike A — is fatiguing.
                Want the full readout?
              </div>
            </div>
            <div>
              <div className="lp-bubble-meta">09:11 · You call in</div>
              <div className="lp-bubble lp-bubble-user">
                &ldquo;Hey, how are the ads doing this week?&rdquo;
              </div>
              <div className="lp-bubble lp-bubble-ai" style={{ marginTop: 8 }}>
                {persona.liveSample}
              </div>
            </div>
            <div>
              <div className="lp-bubble-meta">14:32 · Quick check</div>
              <div className="lp-bubble lp-bubble-user">
                &ldquo;What’s MRR at right now?&rdquo;
              </div>
              <div className="lp-bubble lp-bubble-ai" style={{ marginTop: 8 }}>
                $4,128 MRR. Up $237 month-to-date. Three trials end this week —
                two look likely to convert based on usage.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="lp-card lp-feature" style={{ padding: 22 }}>
              <div
                className="lp-feature-icon"
                style={{ width: 38, height: 38, marginBottom: 12 }}
              >
                <PhoneCall size={18} strokeWidth={1.8} />
              </div>
              <h3 style={{ fontSize: 15 }}>You make the call</h3>
              <p style={{ fontSize: 13 }}>
                Driving, walking, between meetings — call your dedicated number
                and ask anything about your business.
              </p>
            </div>
            <div className="lp-card lp-feature" style={{ padding: 22 }}>
              <div
                className="lp-feature-icon"
                style={{ width: 38, height: 38, marginBottom: 12 }}
              >
                <LineChart size={18} strokeWidth={1.8} />
              </div>
              <h3 style={{ fontSize: 15 }}>It reads your Stripe data</h3>
              <p style={{ fontSize: 13 }}>
                MRR, new trials, churn risk — pulled live the moment you ask.
                No exports, no dashboards.
              </p>
            </div>
            <div className="lp-card lp-feature" style={{ padding: 22 }}>
              <div
                className="lp-feature-icon"
                style={{ width: 38, height: 38, marginBottom: 12 }}
              >
                <BarChart3 size={18} strokeWidth={1.8} />
              </div>
              <h3 style={{ fontSize: 15 }}>And your Facebook Ads</h3>
              <p style={{ fontSize: 13 }}>
                Spend, ROAS, fatiguing creatives, top performers — the same data
                the dashboard shows, just spoken.
              </p>
            </div>
            <div className="lp-card lp-feature" style={{ padding: 22 }}>
              <div
                className="lp-feature-icon"
                style={{ width: 38, height: 38, marginBottom: 12 }}
              >
                <Sun size={18} strokeWidth={1.8} />
              </div>
              <h3 style={{ fontSize: 15 }}>Briefings on a schedule</h3>
              <p style={{ fontSize: 13 }}>
                Morning digest of what moved overnight and what to watch today —
                delivered by voice or email.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
